import {
    type Absence,
    AbsenceStatus,
    dateAdd,
    type DateRange,
    displayDistinctWorkarea,
    type EntityFilterContext,
    formatDateShort,
    formatWeekDayShort,
    GetAbsencesParams,
    getDayOfWeek,
    getHolidayForDate,
    GetRosterNotesParams,
    getToday,
    type Holiday,
    matchesFilters,
    parseDateString,
    type RosterNoteDTO,
    safeGetWeekRange,
    TimeEntry,
    TimeEntryType,
    toDateString,
    toTimeString,
} from "@pentacode/core";
import type { DateString } from "@pentacode/openapi";
import "@pentacode/ui/components";
import { css, html, LitElement, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import "../../components/roster-notes/roster-note";
import { app } from "../../init";
import { Routing } from "../../mixins/routing";
import { StateMixin } from "../../mixins/state";
import { animations, shared } from "../../styles";
import { alert, confirm } from "../alert-dialog";
import "../date-picker";
import "../popover";
import type { Popover } from "../popover";
import "../scroller";
import { canOfferShiftForTrade, getEntriesToMarkSeen, RosterChanges } from "./lib/roster-changes";

interface Day {
    date: DateString;
    entries: TimeEntry[];
    absence?: Absence;
    notes: RosterNoteDTO[];
    isSunday: boolean;
    holiday?: Holiday | null;
}

@customElement("ptc-staff-roster")
export class Roster extends Routing(StateMixin(LitElement)) {
    routePattern = /^(home|roster)/;

    @property({ reflect: true, type: Boolean })
    expanded = false;

    @property({ attribute: false })
    date: DateString;

    get changesCount() {
        return this._entries?.filter(
            (e) => e.published && !!this._changes.getType(e.id, e.published.date, app.profile?.id)
        ).length;
    }

    get availableCount() {
        return this._entries?.filter((e) => this._isAvailable(e, this._entries, this._absences)).length;
    }

    @state()
    private _entries: TimeEntry[];

    @state()
    private _absences: Absence[];

    @state()
    private _notes: RosterNoteDTO[];

    @state()
    private _loading = false;

    private _changes = new RosterChanges();

    private _expanded = new Set<string>();

    private _getRange() {
        return safeGetWeekRange(this.date);
    }

    private _intersectionObserver = new IntersectionObserver(
        (entries: IntersectionObserverEntry[]) => this._intersect(entries),
        { threshold: 0.4 }
    );

    async connectedCallback() {
        super.connectedCallback();
        await app.loaded;
        if (app.account) {
            void this._load();
        }
    }

    async updated(changes: Map<string, unknown>) {
        if (changes.has("date")) {
            void this._load();
        }
        // Expanding only changes opacity, which IntersectionObserver ignores, so re-observe to get a fresh
        // initial callback for the entries that are now actually visible
        if (changes.has("_entries") || (changes.has("expanded") && this.expanded)) {
            this._observeEntries();
        }
        if (changes.has("_entries")) {
            const horizontalRoster = this.renderRoot.querySelector(".horizontal-roster");
            const today = horizontalRoster?.querySelector(
                `[data-date="${toDateString(new Date())}"]`
            ) as HTMLDivElement;
            if (today) {
                horizontalRoster!.scrollLeft = today.offsetLeft - 10;
            }
        }
    }

    refresh() {
        return this._load();
    }

    private _isAvailable(entry: TimeEntry, otherEntries: TimeEntry[], absences: Absence[]) {
        if (!app.profile || !otherEntries || !absences) {
            return false;
        }

        return app.isAvailable(entry, app.profile, otherEntries, absences);
    }

    private async _fetchTimeEntries(range: DateRange) {
        return app.getTimeEntries({
            from: range.from,
            to: range.to,
            type: [
                TimeEntryType.Work,
                TimeEntryType.Free,
                TimeEntryType.CompDay,
                TimeEntryType.Vacation,
                TimeEntryType.Sick,
            ],
            includeDeleted: true,
            includeUnassigned: true,
            includeOffered: true,
        });
    }

    private async _load() {
        if (this._loading) return;

        this._loading = true;

        await app.loaded;

        const company = app.company;
        const profile = app.profile;

        const today = getToday();
        const range = this._getRange();
        if (!company || !profile || !range) {
            return;
        }

        try {
            const [entries, absences, notes] = await Promise.all([
                this._fetchTimeEntries(range),
                app.api.getAbsences(
                    new GetAbsencesParams({
                        from: range.from,
                        to: range.to,
                        employee: [profile.id],
                        status: [AbsenceStatus.Approved, AbsenceStatus.Inferred],
                    })
                ),
                app.api.getRosterNotes(
                    new GetRosterNotesParams({
                        from: today,
                        to: range.to,
                    })
                ),
            ]);

            // deduplicate entries
            this._entries = this._deduplicateEntries(entries)
                .filter((entry, _, arr) => this._isRelevantEntry(entry, arr, absences, today, profile.id))
                .sort((a, b) => ((a.published?.startPlanned || "") < (b.published?.startPlanned || "") ? -1 : 1));
            this._absences = absences;
            this._notes = notes;

            this._changes.track(this._entries);

            this.dispatchEvent(new CustomEvent("roster-loaded"));
        } catch (e) {
            void alert(e.message, { type: "warning" });
        }

        this._loading = false;
    }

    private _deduplicateEntries(entries: TimeEntry[]) {
        return Array.from(
            new Map<string, TimeEntry>(
                entries.map((e) => [
                    `${e.published?.positionId} ${e.published?.date} ${e.published?.startPlanned} - ${e.published?.endPlanned} ${e.published?.employeeId} ${e.published?.deleted}`,
                    e,
                ])
            ).values()
        );
    }

    private _isRelevantEntry(
        entry: TimeEntry,
        entries: TimeEntry[],
        absences: Absence[],
        publishedAfter: DateString,
        profileId: number
    ) {
        return (
            entry.published &&
            (!entry.published.deleted || !entry.isSeen) &&
            // not work, has planned time or is today or in the future
            (entry.type !== TimeEntryType.Work ||
                entry.published.startPlanned ||
                entry.published.endPlanned ||
                entry.published.date >= publishedAfter) &&
            (entry.employeeId === profileId || this._isAvailable(entry, entries, absences))
        );
    }

    private _groupByDay(
        entries: TimeEntry[],
        absences: Absence[],
        notes: RosterNoteDTO[],
        range: DateRange,
        filterContext: EntityFilterContext
    ) {
        // return [] if any dependencies are not loaded yet
        if (!entries || !absences || !notes || !range) {
            return [];
        }

        let date = range.from;
        const days: Day[] = [];

        while (date < range.to) {
            days.push({
                date,
                entries: entries.filter(
                    (e) =>
                        (e.published?.date === date && !e.published.deleted) ||
                        this._changes.getType(e.id, date, app.profile?.id) === "removed"
                ),
                absence: absences.find((a) => a.start <= date && a.end > date),
                notes: notes.filter(
                    (n) =>
                        n.start <= date &&
                        n.end > date &&
                        matchesFilters(n.filters, filterContext) &&
                        n.displayAsRosterNote
                ),
                isSunday: getDayOfWeek(date) === 0,
                holiday: getHolidayForDate(date, {
                    country: app.company!.country,
                    holidays: app.venues[0]?.enabledHolidays,
                    state: app.venues[0]?.state,
                }),
            });
            date = dateAdd(date, { days: 1 });
        }

        return days;
    }

    private _observeEntries() {
        this._intersectionObserver.disconnect();
        this.renderRoot!.querySelectorAll(".vertical-roster .entry").forEach((e) =>
            this._intersectionObserver.observe(e)
        );
    }

    private _intersect(intEntries: IntersectionObserverEntry[]) {
        // In collapsed mode, entries might still be rendered and intersect, but should not be marked as seen
        if (!this.expanded) {
            return;
        }

        const profile = app.profile;
        if (this._loading || !profile) {
            return;
        }

        const visibleEntryIds = intEntries
            .filter((e) => e.isIntersecting)
            .map((e) => (e.target as HTMLElement).dataset.entry!);
        const updated = getEntriesToMarkSeen(this._entries, visibleEntryIds, profile.id);
        for (const entry of updated) {
            entry.setSeen();
        }

        if (updated.length) {
            void app.createOrUpdateTimeEntries(updated, { setUpdated: false });
        }
    }

    private async _takeShift(entry: TimeEntry) {
        if (
            !(await confirm("Wollen Sie diese Schicht übernehmen?", "Übernehmen", "Abbrechen", {
                title: "Schicht Übernehmen",
                icon: "icon.action.claim-shift",
            }))
        ) {
            return;
        }

        this._loading = true;

        try {
            await app.api.takeShift(entry.id);
            this._changes.forget(entry.id);
        } catch (e) {
            void alert(e.message, { type: "warning" });
        }
        this._loading = false;
        void this._load();
    }

    private async _offerUpShift(entry: TimeEntry) {
        if (
            !(await confirm("Wollen Sie diese Schicht zum Tausch freigeben?", "Freigeben", "Abbrechen", {
                title: "Zum Tausch Freigeben",
                icon: "icon.action.offer-shift",
            }))
        ) {
            return;
        }

        this._loading = true;
        try {
            await app.api.offerShiftForTrade(entry.id);
            const range = this._getRange();
            if (range) {
                this._entries = await this._fetchTimeEntries(range);
                this._changes.track(this._entries);
            }
        } catch (e) {
            void alert(e.message, { type: "warning" });
        }
        this._loading = false;
    }

    private async _undoOfferUpShift(entry: TimeEntry) {
        if (
            !(await confirm("Wollen Sie die Freigabe zum Tausch zurücknehmen?", "Zurücknehmen", "Abbrechen", {
                title: "Freigabe Zurücknehmen",
                icon: "icon.action.reset",
            }))
        ) {
            return;
        }

        this._loading = true;
        try {
            entry.offered = null;
            await app.createOrUpdateTimeEntries([entry], { wait: true, setUpdated: false });
        } catch (e) {
            void alert(e.message, { type: "warning" });
        }
        this._loading = false;
    }

    private _setExpanded(id: string, expanded: boolean) {
        expanded ? this._expanded.add(id) : this._expanded.delete(id);
        this.requestUpdate();
    }

    static styles = [
        shared,
        animations,
        css`
            :host {
                display: block;
                position: relative;
                will-change: transform;
            }

            .sticky {
                z-index: 3;
                left: 0;
            }

            .horizontal-roster,
            .vertical-roster {
                will-change: opacity;
                transition: opacity 0.3s;
            }

            :host([expanded]) .horizontal-roster,
            :host(:not([expanded])) .vertical-roster {
                opacity: 0;
                pointer-events: none;
            }

            .entry {
                padding-bottom: 0.2em;
                padding-left: 0.8em;
            }

            .entry.deleted {
                background: linear-gradient(
                    to top left,
                    transparent 0%,
                    transparent 49%,
                    var(--color-highlight) 49.5%,
                    var(--color-highlight) 50.5%,
                    transparent 51%,
                    transparent 100%
                );
            }

            .entry.available {
                border: dashed 1px;
            }

            .entry.offered {
                border: dotted 1px;
            }

            .entry .entry-status {
                padding: 0.2em 0.4em;
                /* Cancel out vertical padding + border so the badge doesn't make its entry taller than others */
                margin: calc(-0.2em - 1px) 0;
                line-height: 1;
                color: var(--color-highlight);
                border: solid 1px;
                border-radius: 0.5em;
            }

            .entry.absence {
                padding: 1em 3em;
                text-align: center;
            }

            .collapsed-icon {
                transition: transform 0.2s;
            }

            .collapsed-icon.expanded {
                transform: rotate(90deg);
            }
        `,
    ];

    private _renderVertical(days: Day[]) {
        const range = this._getRange();
        if (!range) {
            return nothing;
        }

        const today = toDateString(new Date());
        return html`
            <div class="fullbleed vertical layout vertical-roster">
                <ptc-scroller class="stretch" .locked=${!this.expanded}>
                    <div class="vertical layout fill-vertically relative">
                        ${days.map(
                            ({ date, entries, absence, notes, isSunday, holiday }) => html`
                                <div class="stretch padded border-bottom" style="min-height: 5em" data-date=${date}>
                                    <div class="padded background center-aligning horizontal layout">
                                        <div class="stretch ${isSunday ? "orange colored-text" : ""}">
                                            <strong>${formatWeekDayShort(date)}</strong>, ${formatDateShort(date)}
                                        </div>
                                        ${holiday ? html` <div class="violet colored-text">${holiday.name}</div> ` : ""}
                                        ${notes.length
                                            ? html`
                                                  <div
                                                      class="smaller bold pill click left-margined"
                                                      style="--color-highlight: ${notes[0].color || "unset"}"
                                                  >
                                                      <ptc-ui-icon name="icon.object.note"></ptc-ui-icon>
                                                      ${notes.length}
                                                  </div>
                                                  <ptc-popover
                                                      alignment="left-bottom"
                                                      positioning="absolute"
                                                      style="min-width: 16em; max-width: calc(100% - 8em);"
                                                  >
                                                      <ptc-scroller style="max-height: 20em;">
                                                          <div class="spacing vertical layout">
                                                              ${notes.map(
                                                                  (note) =>
                                                                      html`<ptc-roster-note
                                                                          .rosterNote=${note}
                                                                          .employee=${app.profile!}
                                                                      ></ptc-roster-note>`
                                                              )}
                                                          </div>
                                                      </ptc-scroller>
                                                  </ptc-popover>
                                              `
                                            : ""}
                                    </div>

                                    <div class="grid" style="--grid-gap: 0.5em; align-items: start;">
                                        ${entries.length
                                            ? entries.map((e) => this._renderEntry(e, date))
                                            : absence
                                              ? this._renderEntry(
                                                    new TimeEntry({
                                                        type: absence.type,
                                                        employeeId: absence.employeeId,
                                                        date,
                                                    }).setPublished(),
                                                    date
                                                )
                                              : html` <div class="padded semibold faded">Keine Einträge</div> `}
                                    </div>
                                </div>
                            `
                        )}
                    </div>
                </ptc-scroller>

                <div class="padded horizontal center-aligning spacing layout">
                    <div class="horizontal layout" style="min-width: 8em;">
                        <button
                            class="transparent"
                            type="button"
                            @click=${() => this.go(null, { date: dateAdd(this.date, { days: -7 }) })}
                        >
                            <ptc-ui-icon name="icon.navigation.back"></ptc-ui-icon>
                        </button>
                        <button
                            class="transparent"
                            type="button"
                            @click=${() => this.go(null, { date: today })}
                            ?disabled=${this.date === today}
                        >
                            <ptc-ui-icon name="icon.object.day-view"></ptc-ui-icon>
                        </button>
                    </div>
                    <div class="centering horizontal layout stretch">
                        <button class="transparent">
                            <strong>${formatDateShort(range.from)}</strong> -
                            <strong>${formatDateShort(dateAdd(range.to, { days: -1 }))}</strong> ${parseDateString(
                                this.date
                            )?.getFullYear()}
                        </button>
                        <ptc-popover positioning="absolute" alignment="top">
                            <ptc-date-picker
                                mode="week"
                                .value=${this.date}
                                @change=${(e: CustomEvent<{ value: string }>) => {
                                    this.go(null, { date: e.detail.value });
                                    ((e.target as HTMLElement).parentElement as Popover).hide();
                                }}
                            ></ptc-date-picker>
                        </ptc-popover>
                    </div>
                    <div class="horizontal end-justifying layout" style="min-width: 8em;">
                        <button
                            class="transparent"
                            type="button"
                            @click=${() => this.go(null, { date: dateAdd(this.date, { days: 7 }) })}
                        >
                            <ptc-ui-icon name="icon.navigation.forward"></ptc-ui-icon>
                        </button>
                    </div>
                </div>
            </div>
        `;
    }

    private _renderHorizontal(days: Day[]) {
        return html`
            <div class="horizontal scroller horizontal-roster hide-scrollbar" style="margin-top: -1em;">
                <div class="margined horizontally-padded spacing start-aligning horizontal layout">
                    ${days.map(
                        ({ date, entries, absence }) => html`
                            <div class="start-aligning vertical layout" data-date=${date}>
                                <div class="padded background blue colored-text sticky">
                                    <strong>${formatWeekDayShort(date)}</strong>, ${formatDateShort(date)}
                                </div>

                                <div class="smaller spacing horizontal layout">
                                    ${entries.length
                                        ? entries.map((e) => this._renderEntry(e, date))
                                        : absence
                                          ? this._renderEntry(
                                                new TimeEntry({
                                                    type: absence.type,
                                                    employeeId: absence.employeeId,
                                                    date,
                                                }).setPublished(),
                                                date
                                            )
                                          : html` <div class="double-padded semibold faded">Keine Einträge</div> `}
                                </div>
                            </div>
                        `
                    )}
                </div>
            </div>
        `;
    }

    private _renderEntry(entry: TimeEntry, date: string) {
        const change = entry.published && this._changes.getType(entry.id, date, app.profile?.id);
        if (!entry.published || (entry.published.deleted && !change) || !app.company || !app.profile) {
            return;
        }
        const { positionId, startPlanned, endPlanned, comment } = entry.published;
        const previous = this._changes.getPrevious(entry);
        const position = positionId ? app.getPosition(positionId)?.position : null;
        const available = this._isAvailable(entry, this._entries, this._absences);
        const offered = entry.offered && entry.offered > entry.updated && (!entry.taken || entry.offered > entry.taken);
        const collapsed = !this.expanded || !this._expanded.has(entry.id);
        const canOffer = app.hasPermission("staff.roster.offer") && canOfferShiftForTrade(startPlanned);

        const startTimeStr = toTimeString(startPlanned) || "offen";
        const endTimeStr = toTimeString(endPlanned) || "offen";

        if (!position) {
            return html`
                <div
                    class="padded box entry absence"
                    style="--color-highlight: ${app.getTimeEntryColor(entry)}"
                    data-entry=${entry.id}
                >
                    <div class="large">${app.localized.timeEntryTypeLabel(entry)}</div>
                </div>
            `;
        } else if (available) {
            return html`
                <div
                    class="padded box entry strong shining available"
                    style="--color-highlight: ${app.getTimeEntryColor(entry)}"
                    data-entry=${entry.id}
                    @mouseenter=${() => this._setExpanded(entry.id, true)}
                    @mouseleave=${() => this._setExpanded(entry.id, false)}
                >
                    <div class="horizontal spacing center-aligning layout">
                        <div class="stretch ellipsis">
                            ${displayDistinctWorkarea(app.company, app.profile, position)}
                        </div>
                        <div class="smaller purple entry-status">verfügbar</div>
                        <ptc-ui-icon
                            name="icon.toggle.expand"
                            class="smaller collapsed-icon ${collapsed ? "" : "expanded"}"
                        ></ptc-ui-icon>
                    </div>

                    <div class="huger semibold">${startTimeStr} - ${endTimeStr}</div>

                    ${comment
                        ? html`
                              <div>
                                  <pre><ptc-ui-icon name="icon.object.comment"></ptc-ui-icon> ${comment}</pre>
                              </div>
                          `
                        : ""}

                    <ptc-ui-collapse .collapsed=${collapsed}>
                        <div class="tiny spacer"></div>
                        <div class="horizontal end-justifying layout">
                            <button class="smaller subtle" @click=${() => this._takeShift(entry)}>
                                <ptc-ui-icon name="icon.action.claim-shift"></ptc-ui-icon> Übernehmen
                            </button>
                        </div>
                        <div class="tiny spacer"></div>
                    </ptc-ui-collapse>
                </div>
            `;
        } else if (offered) {
            return html`
                <div
                    class="padded box entry strong offered"
                    style="--color-highlight: ${app.getTimeEntryColor(entry)}"
                    data-entry=${entry.id}
                    @mouseenter=${() => this._setExpanded(entry.id, true)}
                    @mouseleave=${() => this._setExpanded(entry.id, false)}
                >
                    <div class="horizontal spacing center-aligning layout">
                        <div class="stretch ellipsis">
                            ${displayDistinctWorkarea(app.company, app.profile, position)}
                        </div>
                        <div class="smaller purple entry-status">zum Tausch freigegeben</div>
                        <ptc-ui-icon
                            name="icon.toggle.expand"
                            class="smaller collapsed-icon ${collapsed ? "" : "expanded"}"
                        ></ptc-ui-icon>
                    </div>

                    <div class="huger semibold">${startTimeStr} - ${endTimeStr}</div>

                    ${comment
                        ? html`
                              <div>
                                  <pre><ptc-ui-icon name="icon.object.comment"></ptc-ui-icon> ${comment}</pre>
                              </div>
                          `
                        : ""}

                    <ptc-ui-collapse .collapsed=${collapsed} ?hidden=${!app.hasPermission("staff.roster.offer")}>
                        <div class="tiny spacer"></div>
                        <div class="horizontal end-justifying layout">
                            <button class="smaller subtle" @click=${() => this._undoOfferUpShift(entry)}>
                                <ptc-ui-icon name="icon.action.reset"></ptc-ui-icon> Freigabe Zurücknehmen
                            </button>
                        </div>
                        <div class="tiny spacer"></div>
                    </ptc-ui-collapse>
                </div>
            `;
        } else if (change === "removed") {
            return html`
                <div
                    class="padded box entry strong shining deleted"
                    style="--color-highlight: ${app.getTimeEntryColor(entry)}"
                    data-entry=${entry.id}
                >
                    <div class="horizontal spacing center-aligning layout">
                        <div class="stretch ellipsis">
                            ${displayDistinctWorkarea(app.company, app.profile, position)}
                        </div>
                        <div class="smaller orange entry-status">gestrichen</div>
                    </div>
                    <div class="huger semibold">
                        ${toTimeString(previous?.startPlanned) || "offen"} -
                        ${toTimeString(previous?.endPlanned) || "offen"}
                    </div>

                    ${previous?.comment
                        ? html`
                              <div>
                                  <pre><ptc-ui-icon name="icon.object.comment"></ptc-ui-icon> ${previous.comment}</pre>
                              </div>
                          `
                        : ""}
                </div>
            `;
        } else if (change === "changed") {
            return html`
                <div class="center-aligning horizontal layout" style="grid-column: span 2;">
                    <div
                        class="padded box entry strong shining deleted stretch"
                        style="--color-highlight: ${app.getTimeEntryColor(entry)}"
                        data-entry=${entry.id}
                    >
                        <div class="horizontal spacing center-aligning layout">
                            <div class="stretch ellipsis">
                                ${displayDistinctWorkarea(app.company, app.profile, position)}
                            </div>
                            <div class="smaller orange entry-status">geändert</div>
                        </div>
                        <div class="huger semibold">
                            ${toTimeString(previous?.startPlanned) || "offen"} -
                            ${toTimeString(previous?.endPlanned) || "offen"}
                        </div>

                        ${previous?.comment
                            ? html`
                                  <div>
                                      <pre><ptc-ui-icon name="icon.object.comment"></ptc-ui-icon> ${previous.comment}</pre>
                                  </div>
                              `
                            : ""}
                    </div>

                    <ptc-ui-icon name="icon.navigation.forward" class="changed-arrow"></ptc-ui-icon>

                    <div
                        class="padded box entry strong shining stretch"
                        style="--color-highlight: ${app.getTimeEntryColor(entry)}"
                        data-entry=${entry.id}
                        @mouseenter=${() => this._setExpanded(entry.id, true)}
                        @mouseleave=${() => this._setExpanded(entry.id, false)}
                    >
                        <div class="horizontal spacing center-aligning layout">
                            <div class="stretch ellipsis">
                                ${displayDistinctWorkarea(app.company, app.profile, position)}
                            </div>
                            <div class="smaller orange entry-status">geändert</div>
                            <ptc-ui-icon
                                name="icon.toggle.expand"
                                class="smaller collapsed-icon ${collapsed ? "" : "expanded"}"
                            ></ptc-ui-icon>
                        </div>

                        <div class="huger semibold">${startTimeStr} - ${endTimeStr}</div>

                        ${comment
                            ? html`
                                  <div>
                                      <pre><ptc-ui-icon name="icon.object.comment"></ptc-ui-icon> ${comment}</pre>
                                  </div>
                              `
                            : ""}

                        <ptc-ui-collapse .collapsed=${collapsed} ?hidden=${!canOffer}>
                            <div class="tiny spacer"></div>
                            <div class="horizontal end-justifying layout">
                                <button class="smaller subtle" @click=${() => this._offerUpShift(entry)}>
                                    <ptc-ui-icon name="icon.action.offer-shift"></ptc-ui-icon> Zum Tausch Freigeben
                                </button>
                            </div>
                            <div class="tiny spacer"></div>
                        </ptc-ui-collapse>
                    </div>
                </div>
            `;
        } else if (change === "added") {
            return html`
                <div
                    class="padded box entry strong shining"
                    style="--color-highlight: ${app.getTimeEntryColor(entry)}"
                    data-entry=${entry.id}
                    @mouseenter=${() => this._setExpanded(entry.id, true)}
                    @mouseleave=${() => this._setExpanded(entry.id, false)}
                >
                    <div class="horizontal spacing center-aligning layout">
                        <div class="stretch ellipsis">
                            ${displayDistinctWorkarea(app.company, app.profile, position)}
                        </div>
                        <div class="smaller orange entry-status">neu</div>
                        <ptc-ui-icon
                            name="icon.toggle.expand"
                            class="smaller collapsed-icon ${collapsed ? "" : "expanded"}"
                        ></ptc-ui-icon>
                    </div>

                    <div class="huger semibold">${startTimeStr} - ${endTimeStr}</div>

                    ${comment
                        ? html`
                              <div>
                                  <pre><ptc-ui-icon name="icon.object.comment"></ptc-ui-icon> ${comment}</pre>
                              </div>
                          `
                        : ""}

                    <ptc-ui-collapse .collapsed=${collapsed} ?hidden=${!canOffer}>
                        <div class="tiny spacer"></div>
                        <div class="horizontal end-justifying layout">
                            <button class="smaller subtle" @click=${() => this._offerUpShift(entry)}>
                                <ptc-ui-icon name="icon.action.offer-shift"></ptc-ui-icon> Zum Tausch Freigeben
                            </button>
                        </div>
                        <div class="tiny spacer"></div>
                    </ptc-ui-collapse>
                </div>
            `;
        } else {
            return html`
                <div
                    class="padded box entry"
                    style="--color-highlight: ${app.getTimeEntryColor(entry)}"
                    data-entry=${entry.id}
                    @mouseenter=${() => this._setExpanded(entry.id, true)}
                    @mouseleave=${() => this._setExpanded(entry.id, false)}
                >
                    <div class="horizontal spacing center-aligning layout">
                        <div class="stretch ellipsis">
                            ${displayDistinctWorkarea(app.company, app.profile, position)}
                        </div>
                        <ptc-ui-icon
                            name="icon.toggle.expand"
                            class="smaller collapsed-icon ${collapsed ? "" : "expanded"}"
                        ></ptc-ui-icon>
                    </div>

                    <div class="huger semibold">${startTimeStr} - ${endTimeStr}</div>

                    ${comment
                        ? html`
                              <div>
                                  <pre><ptc-ui-icon name="icon.object.comment"></ptc-ui-icon> ${comment}</pre>
                              </div>
                          `
                        : ""}

                    <ptc-ui-collapse .collapsed=${collapsed} ?hidden=${!canOffer}>
                        <div class="tiny spacer"></div>
                        <div class="horizontal end-justifying layout">
                            <button class="smaller subtle" @click=${() => this._offerUpShift(entry)}>
                                <ptc-ui-icon name="icon.action.offer-shift"></ptc-ui-icon> Zum Tausch Freigeben
                            </button>
                        </div>
                        <div class="tiny spacer"></div>
                    </ptc-ui-collapse>
                </div>
            `;
        }
    }

    render() {
        const range = this._getRange();
        if (!app.account || !app.profile || !range) {
            return;
        }

        const days = this._groupByDay(this._entries, this._absences, this._notes, range, {
            company: app.company,
            employee: app.profile,
        });

        return html`
            ${this._renderVertical(days)} ${this._renderHorizontal(days)}

            <div class="fullbleed centering layout scrim" ?hidden=${!this._loading}>
                <ptc-ui-spinner ?active=${this._loading}></ptc-ui-spinner>
            </div>
        `;
    }
}
